/**
 * Client-side API helper.
 * Fetches from Next.js API routes only (no direct backend access).
 * Uses credentials: "include" for auth cookies.
 *
 * Optional timeout: use for session/layout-style calls so slow networks
 * get a clear retry UX instead of long hangs (see SESSION_LAYOUT_TIMEOUT_MS).
 */

/** Timeout for session/layout-style calls (e.g. /api/session). Use with clientGet options. */
export const SESSION_LAYOUT_TIMEOUT_MS = 10_000;

export class ClientApiTimeoutError extends Error {
  readonly isTimeout = true;
  constructor(message = "Request timed out") {
    super(message);
    this.name = "ClientApiTimeoutError";
  }
}

function buildSearchParams(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") {
      search.set(k, String(v));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export interface ClientGetOptions {
  /** Abort request after this many ms. Use for session/layout calls to avoid long hangs. */
  timeoutMs?: number;
}

export async function clientGet<T>(
  path: string,
  params?: Record<string, unknown>,
  options?: ClientGetOptions
): Promise<T> {
  const qs = params ? buildSearchParams(params) : "";
  const url = `${path}${qs}`;
  const timeoutMs = options?.timeoutMs;
  const controller = timeoutMs ? new AbortController() : undefined;
  const timeoutId =
    timeoutMs &&
    controller &&
    setTimeout(() => controller.abort(), timeoutMs);

  try {
    const resp = await fetch(url, {
      credentials: "include",
      cache: "no-store",
      signal: controller?.signal,
    });
    if (timeoutId) clearTimeout(timeoutId);
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`API error ${resp.status}: ${text.slice(0, 200)}`);
    }
    return resp.json() as Promise<T>;
  } catch (err) {
    if (timeoutId) clearTimeout(timeoutId);
    if (err instanceof Error && err.name === "AbortError") {
      throw new ClientApiTimeoutError(
        `Request timed out after ${timeoutMs}ms`
      );
    }
    throw err;
  }
}
